import dayjs from "dayjs";
import apiClient from "../../../helpers/apiClient";
import { getApiBaseUrl } from "../../../helpers/adminAuth";

export const INSURANCE_SALE_PROVIDERS = ["teqmobility", "bonzah"];

export const PROVIDER_LABELS = {
    teqmobility: "TeqMobility",
    bonzah: "Bonzah",
};

/**
 * The company toggle stores providers lowercase ("teqmobility"), while the
 * addons-insurance catalog and the reservation's insuranceType use camelCase
 * ("teqMobility"). Everything in this module speaks the lowercase form.
 */
export const isProviderEnabled = (myCompanyData, provider) =>
    Boolean(
        myCompanyData?.insurance?.some(
            (item) =>
                String(item.provider).toLowerCase() === String(provider).toLowerCase() &&
                (item.enabled === true || item.enabled === "true"),
        ),
    );

export const isBonzahEnabled = (myCompanyData) => isProviderEnabled(myCompanyData, "bonzah");

export const getEnabledSaleProviders = (myCompanyData) =>
    INSURANCE_SALE_PROVIDERS.filter((provider) => isProviderEnabled(myCompanyData, provider));

const toCatalogProvider = (provider) =>
    String(provider).toLowerCase() === "teqmobility" ? "teqMobility" : String(provider).toLowerCase();

export const quoteInsuranceSale = async (payload) => {
    const response = await apiClient.post("/api/insurance-sale/quote", payload);
    return response?.data?.data ?? null;
};

export const createInsuranceSale = async (payload) => {
    const response = await apiClient.post("/api/insurance-sale/create", payload);
    return response?.data;
};

export const isBookingAgentUrlConfigured = () =>
    Boolean(String(process.env.REACT_APP_BOOKING_AGENT_URL || "").trim());

export const startInsuranceSaleCoverage = async (reservationId) => {
    const response = await apiClient.post(`/api/insurance-sale/${reservationId}/start`);
    return response?.data;
};

export const quoteExtendInsuranceSale = async (reservationId, dropoffDate) => {
    const response = await apiClient.post(`/api/insurance-sale/${reservationId}/quote-extend`, {
        dropoffDate,
    });
    return response?.data?.data ?? null;
};

export const extendInsuranceSale = async (reservationId, payload) => {
    const response = await apiClient.post(`/api/insurance-sale/${reservationId}/extend`, payload);
    return response?.data;
};

export const endInsuranceSale = async (reservationId) => {
    const response = await apiClient.post(`/api/insurance-sale/${reservationId}/end`);
    return response?.data;
};

export const createInsuranceSaleCheckout = async ({
    reservationId,
    amount,
    customerEmail,
    companyId,
    reservationProcess,
    extensionDropoffDate,
    successUrl,
    cancelUrl,
}) => {
    const response = await apiClient.post(
        "/api/payment-v2/create-deposit-payment-intent",
        {
            reservationId,
            amount,
            customerEmail,
            successUrl,
            cancelUrl,
            metadata: {
                reservationId,
                companyId,
                reservationProcess,
                ...(extensionDropoffDate ? { extensionDropoffDate } : {}),
            },
        },
        companyId ? { headers: { "company-id": companyId } } : undefined,
    );
    return response?.data;
};

export const buildInsuranceSaleCustomerReturnUrl = (
    companyId,
    reservationId,
    payment,
    reservationProcess,
) => {
    const baseUrl = process.env.REACT_APP_BOOKING_AGENT_URL;
    const params = new URLSearchParams({
        success: payment === "success" ? "true" : "false",
        resID: reservationId,
        reservationProcess,
        paymentSuccess: payment === "success" ? "true" : "false",
        isDeposit: "true",
        paymentPlatform: "stripe",
    });
    return `${baseUrl}/${companyId}/confirmation?${params.toString()}`;
};

export const sendInsuranceSalePaymentLink = async ({
    reservationId,
    amount,
    customerEmail,
    companyId,
    reservationProcess,
    extensionDropoffDate,
}) => {
    if (!customerEmail?.trim()) {
        throw new Error("Customer email is required");
    }

    const checkoutResponse = await createInsuranceSaleCheckout({
        reservationId,
        amount,
        customerEmail: customerEmail.trim(),
        companyId,
        reservationProcess,
        extensionDropoffDate,
        successUrl: buildInsuranceSaleCustomerReturnUrl(
            companyId,
            reservationId,
            "success",
            reservationProcess,
        ),
        cancelUrl: buildInsuranceSaleCustomerReturnUrl(
            companyId,
            reservationId,
            "cancel",
            reservationProcess,
        ),
    });

    if (checkoutResponse?.success === false) {
        throw new Error(checkoutResponse?.message || "Could not create payment session");
    }

    const paymentUrl = checkoutResponse?.data?.paymentUrl;
    if (!paymentUrl) {
        throw new Error("Payment URL not received from checkout session");
    }

    const response = await apiClient.post(
        "/api/payment-v2/send-payment-link",
        {
            reservationId,
            customerEmail: customerEmail.trim(),
            paymentUrl,
        },
        companyId ? { headers: { "company-id": companyId } } : undefined,
    );

    return response?.data;
};

export const getCompanyId = (myCompanyData) => myCompanyData?.id || myCompanyData?._id;

export const isSalePaid = (record) =>
    record?.isPaid === true || Number(record?.invoice?.due ?? 0) <= 0;

export const getProviderKey = (record) =>
    String(record?.insuranceType ?? "").toLowerCase() === "teqmobility" ? "teqmobility" : "bonzah";

export const getPolicy = (record) =>
    getProviderKey(record) === "teqmobility" ? record?.teqMobilityInsurance : record?.bonzahInsurance;

export const getSalePolicyStatus = (record) => String(getPolicy(record)?.status ?? "").toLowerCase();

export const listInsuranceSales = async (params = {}) => {
    const response = await apiClient.get("/api/insurance-sale/list", {
        params: {
            page: params.page ?? 1,
            limit: params.limit ?? 20,
            // The backend lists active sales by default; ended ones are checked out to past.
            ...(params.status === "past" ? { status: "past" } : {}),
            ...(params.searchTerm?.trim() ? { searchTerm: params.searchTerm.trim() } : {}),
            ...(params.insuranceType ? { insuranceType: params.insuranceType } : {}),
            ...(params.isPaid !== undefined && params.isPaid !== ""
                ? { isPaid: params.isPaid }
                : {}),
        },
    });

    const data = response?.data;
    return {
        result: Array.isArray(data?.data) ? data.data : [],
        total: data?.meta?.total ?? 0,
    };
};

const FALLBACK_COVERAGE_STATES = [
    { abbreviation: "PA", fullName: "Pennsylvania" },
    { abbreviation: "CO", fullName: "Colorado" },
    { abbreviation: "CT", fullName: "Connecticut" },
    { abbreviation: "NV", fullName: "Nevada" },
    { abbreviation: "SC", fullName: "South Carolina" },
    { abbreviation: "MD", fullName: "Maryland" },
    { abbreviation: "GA", fullName: "Georgia" },
    { abbreviation: "CA", fullName: "California" },
    { abbreviation: "TX", fullName: "Texas" },
    { abbreviation: "AZ", fullName: "Arizona" },
    { abbreviation: "FL", fullName: "Florida" },
    { abbreviation: "HI", fullName: "Hawaii" },
    { abbreviation: "IL", fullName: "Illinois" },
    { abbreviation: "IN", fullName: "Indiana" },
    { abbreviation: "MO", fullName: "Missouri" },
    { abbreviation: "OR", fullName: "Oregon" },
    { abbreviation: "TN", fullName: "Tennessee" },
];

export const fetchTeqMobilityCoverageStates = async () => {
    try {
        const response = await apiClient.get("/api/insurance/teqMobility-coverage-states");
        const states = response?.data?.success ? response.data.data?.states : null;
        return states?.length ? states : FALLBACK_COVERAGE_STATES;
    } catch {
        return FALLBACK_COVERAGE_STATES;
    }
};

export const isStateCoveredByTeqMobility = (state, coverageStates = []) => {
    const normalized = String(state ?? "").trim();
    if (!normalized || coverageStates.length === 0) return false;

    const upper = normalized.toUpperCase();

    return coverageStates.some((coveredState) => {
        const fullName = String(coveredState?.fullName ?? "").trim();
        const abbreviation = String(coveredState?.abbreviation ?? "").trim().toUpperCase();

        return (
            (fullName && fullName.toLowerCase() === normalized.toLowerCase()) ||
            (abbreviation && abbreviation === upper)
        );
    });
};

/**
 * Resolve TeqMobility coverage from the coverage-states API (same source as the
 * location form state picker). Returns null when coverage data is not loaded yet.
 */
export const resolveLocationTeqCoverage = (location, coverageStates = []) => {
    if (!location) return false;
    if (!coverageStates.length) return null;

    return isStateCoveredByTeqMobility(location.state, coverageStates);
};

/** Merge TeqMobility coverage onto each location using the coverage-states API. */
export const enrichLocationsWithTeqCoverage = (locations, coverageStates = []) =>
    (Array.isArray(locations) ? locations : []).map((location) => {
        const resolved = resolveLocationTeqCoverage(location, coverageStates);
        if (resolved === null) return location;

        return {
            ...location,
            isInsuranceEnabled: resolved,
        };
    });

/** Prefer coverage-states API; fall back to the backend flag on the location record. */
export const isLocationTeqMobilityEnabled = (location, coverageStates = []) => {
    if (!location) return false;

    const resolved = resolveLocationTeqCoverage(location, coverageStates);
    if (resolved !== null) return resolved;

    return location.isInsuranceEnabled === true || location.isInsuranceEnabled === "true";
};

/** Insurance products for one provider. carValue=1 keeps the response light: the real
 * price comes from the quote endpoint once a car and dates are known. */
export const listInsuranceProducts = async (provider, carValue = 1) => {
    const response = await apiClient.get("/api/addons-insurance/list", {
        params: {
            limit: 100,
            page: 1,
            status: "active",
            carValue: carValue > 0 ? carValue : 1,
            ...(provider ? { provider: toCatalogProvider(provider) } : {}),
        },
    });

    if (response?.data?.success) {
        const result = response.data.data?.result ?? response.data.data ?? [];
        return Array.isArray(result) ? result : [];
    }

    return [];
};

/**
 * TeqMobility can only cover a vehicle it has already registered, so that provider is
 * restricted to linked cars. Bonzah rates any car from its details.
 * When dates are provided, uses the same availability API as the booking site.
 */
export const listSellableCars = async (provider, locationId, pickupDate, dropoffDate) => {
    const isTeq = String(provider).toLowerCase() === "teqmobility";

    if (pickupDate && dropoffDate && locationId) {
        const response = await apiClient.get("/api/car/available-cars", {
            params: {
                pickupDate: new Date(pickupDate).toISOString(),
                dropoffDate: new Date(dropoffDate).toISOString(),
                location: locationId,
            },
        });

        if (response?.data?.success) {
            const pricingMode = response?.data?.data?.pricingMode;
            const results = response?.data?.data?.results ?? [];
            let cars =
                pricingMode === "category"
                    ? results.flatMap((cat) =>
                          (cat.cars || []).map((car) => ({
                              ...car,
                              category: car.category || cat,
                          })),
                      )
                    : results;

            if (isTeq) {
                cars = cars.filter((car) => car.insuranceVehicleInfo?.active);
            }

            return cars;
        }

        return [];
    }

    const response = await apiClient.get("/api/car/list", {
        params: {
            limit: 200,
            page: 1,
            ...(locationId ? { location: locationId } : {}),
            ...(isTeq ? { "insuranceVehicleInfo.active": true } : {}),
        },
    });

    if (response?.data?.success) {
        return response.data.data?.result ?? [];
    }

    return [];
};

export const buildInsuranceSalesReturnUrl = (reservationId, payment, process) => {
    const params = new URLSearchParams({
        saleId: reservationId,
        payment,
        process,
    });
    return `${window.location.origin}/sell-insurance?${params.toString()}`;
};

export const getInsuranceCustomerUserId = (user) => user?.id ?? user?.userUID ?? null;

const toFormDate = (value) => {
    if (!value) return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};

export const mapInsuranceCustomerFormValues = (user, states = []) => {
    const rawLicenseState = user?.drivingLicense?.state ?? "";
    const matchedState = states.find(
        (state) =>
            state.isoCode === rawLicenseState ||
            String(state.name).toLowerCase() === String(rawLicenseState).toLowerCase(),
    );

    return {
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
        email: user?.email ?? "",
        phone: user?.mobileNumber ?? "",
        dateOfBirth: toFormDate(user?.drivingLicense?.birthDate || user?.dateOfBirth),
        licenseNumber: user?.drivingLicense?.number ?? "",
        licenseState: matchedState?.isoCode ?? rawLicenseState,
        licenseExpiration: toFormDate(user?.drivingLicense?.expireDate),
        addressLine1: user?.address ?? "",
        zipCode: user?.zipCode ?? "",
    };
};

export const getBonzahCertificateUrl = (record) => {
    const policy = record?.bonzahInsurance || getPolicy(record);
    if (!policy) return null;
    return policy.cardUrl || policy.policyInfo?.documents?.certificateUrl || null;
};

const getBackendBaseUrl = () => String(getApiBaseUrl() || "").replace(/\/$/, "");

const postBlobWithXhr = (path, body) =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${getBackendBaseUrl()}${path}`);
        xhr.responseType = "blob";
        xhr.withCredentials = true;
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("x-softcar-platform", "admin");

        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) {
                xhr.setRequestHeader("X-Client-Timezone", timezone);
            }
        } catch {
            // ignore timezone lookup failures
        }

        xhr.onload = () => {
            const contentType = xhr.getResponseHeader("content-type") || "";
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve({
                    data: xhr.response,
                    headers: { "content-type": contentType },
                    status: xhr.status,
                });
                return;
            }

            const error = new Error("Failed to download PDF");
            error.response = {
                status: xhr.status,
                data: xhr.response,
                headers: { "content-type": contentType },
            };
            reject(error);
        };

        xhr.onerror = () => reject(new Error("Network error while downloading PDF"));
        xhr.onabort = () => reject(new Error("Download cancelled"));
        xhr.send(JSON.stringify(body));
    });

export const savePdfBlobDownload = (blob, filename) => {
    const pdfBlob =
        blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" });

    if (!pdfBlob.size) {
        throw new Error("Empty PDF file received");
    }

    const url = window.URL.createObjectURL(pdfBlob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export const downloadBonzahPolicyPdf = async (reservationId) =>
    postBlobWithXhr("/api/insurance/download-bonzah-pdf", { reservationId });

/** A blob response can still be a JSON error body, so it has to be unwrapped. */
export const parseBlobErrorMessage = async (blob) => {
    try {
        const text = await blob.text();
        const json = JSON.parse(text);
        return json.message || json.error || text;
    } catch {
        return null;
    }
};

export const downloadBonzahPolicyPdfFile = async (reservationId, filename) => {
    const response = await downloadBonzahPolicyPdf(reservationId);
    const contentType = String(
        response.headers["content-type"] || response.headers["Content-Type"] || "",
    ).toLowerCase();
    const blob =
        response.data instanceof Blob
            ? response.data
            : new Blob([response.data], { type: "application/pdf" });

    if (contentType.includes("application/json")) {
        throw new Error(
            (await parseBlobErrorMessage(blob)) || "Failed to download the policy",
        );
    }

    if (!blob.size) {
        throw new Error("Empty PDF file received");
    }

    const header = await blob.slice(0, 4).text();
    if (header !== "%PDF") {
        throw new Error("Invalid PDF file received");
    }

    savePdfBlobDownload(blob, filename);
};

export const listLocations = async () => {
    const response = await apiClient.get("/api/location/list", {
        params: { limit: 200, page: 1 },
    });

    if (response?.data?.success) {
        const payload = response.data.data;
        const result = Array.isArray(payload) ? payload : payload?.result ?? [];
        return Array.isArray(result) ? result : [];
    }

    return [];
};

export const getCarSaleLabel = (car) => {
    const parts = [car?.config?.year, car?.config?.make, car?.config?.model].filter(Boolean);
    const descriptor = parts.length ? parts.join(" ") : car?.carName;
    return [car?.carNumber, descriptor].filter(Boolean).join(" — ") || car?.VIN || "Vehicle";
};

export const getMatchingPricingRange = (pricingRanges, carValue) => {
    if (!Array.isArray(pricingRanges) || pricingRanges.length === 0) {
        return null;
    }

    if (carValue == null) {
        return pricingRanges[0];
    }

    const numericValue = Number(carValue);

    return (
        pricingRanges.find((range) => {
            const min = Number(range?.minCarValue ?? 0);
            const max = range?.maxCarValue != null ? Number(range.maxCarValue) : null;
            return numericValue >= min && (max == null || numericValue <= max);
        }) ?? pricingRanges[0]
    );
};

export const getCatalogDailyPrice = (product, carValue) => {
    const range = getMatchingPricingRange(product?.pricingRanges, carValue);
    if (!range) {
        return null;
    }

    const base = Number(range.baseprice ?? 0);
    const upcharge = range.upchargeAllowed !== false ? Number(range.upchargeAmount ?? 0) : 0;
    return base + upcharge;
};

export const getMissingInsuranceDetails = (user, provider, companyData) => {
    if (!user) return [];

    const license = user.drivingLicense ?? {};
    const missing = [];

    if (!license.number) missing.push("licence number");
    if (!license.state) missing.push("licence state");
    if (!license.expireDate) missing.push("licence expiration");
    if (!license.birthDate && !user.dateOfBirth) missing.push("date of birth");
    if (!user.email) missing.push("email");
    if (!user.mobileNumber) missing.push("mobile number");

    if (String(provider).toLowerCase() === "bonzah") {
        const hasAddress = Boolean(
            user.address?.trim() || companyData?.address?.address?.trim(),
        );
        const hasZip = Boolean(
            user.zipCode?.trim() || companyData?.address?.zip?.trim(),
        );

        if (!hasAddress) missing.push("street address");
        if (!hasZip) missing.push("ZIP code");
    }

    return missing;
};

const formatFormDate = (value) => {
    if (!value) return undefined;
    if (typeof value === "string") return value;
    return value.format?.("YYYY-MM-DD") ?? undefined;
};

const buildInsuranceCustomerPayload = (values) => {
    const firstName = values.firstName?.trim() ?? "";
    const lastName = values.lastName?.trim() ?? "";
    const dateOfBirth = formatFormDate(values.dateOfBirth);

    return {
        firstName,
        lastName,
        name: `${firstName} ${lastName}`.trim(),
        email: values.email?.trim() ?? "",
        mobileNumber: values.phone?.trim() ?? "",
        address: values.addressLine1?.trim() ?? "",
        zipCode: values.zipCode?.trim() ?? "",
        dateOfBirth,
        drivingLicense: {
            firstName,
            lastName,
            number: values.licenseNumber?.trim() ?? "",
            state: values.licenseState ?? "",
            birthDate: dateOfBirth,
            expireDate: formatFormDate(values.licenseExpiration),
        },
    };
};

export const createCustomerForInsurance = async (values) => {
    const response = await apiClient.post("/api/user/create", buildInsuranceCustomerPayload(values));

    if (response?.data?.success === false) {
        throw new Error(response?.data?.message || "Failed to create customer");
    }

    return response?.data?.data;
};

export const updateCustomerForInsurance = async (userId, values) => {
    const response = await apiClient.patch(
        `/api/user/${userId}`,
        buildInsuranceCustomerPayload(values),
    );

    if (response?.data?.success === false) {
        throw new Error(response?.data?.message || "Failed to update customer");
    }

    return response?.data?.data;
};

const MONGO_OBJECT_ID_REGEX = /^[a-f\d]{24}$/i;

const isMongoObjectId = (value) =>
    typeof value === "string" && MONGO_OBJECT_ID_REGEX.test(value);

/** Mongo ObjectId for location API calls — not the UUID `locationId`. */
export const getLocationMongoId = (location) => {
    if (!location) return undefined;
    if (typeof location === "string") {
        return isMongoObjectId(location) ? location : undefined;
    }

    if (isMongoObjectId(location._id)) return location._id;
    if (isMongoObjectId(location.id)) return location.id;
    return undefined;
};

export const getLocationLabel = (location) =>
    [location?.address, location?.city, location?.state].filter(Boolean).join(", ") ||
    [location?.city, location?.state].filter(Boolean).join(", ") ||
    "Location";

export const formatCurrency = (value) => {
    const amount = Number(value ?? 0);
    return `$${amount.toFixed(2)}`;
};

export const INSURANCE_SALE_TIME_PICKER = {
    format: "HH:mm",
    minuteStep: 30,
};

export const roundToHalfHour = (value) => {
    if (!value) return value;

    const minutes = value.minute();
    if (minutes < 15) {
        return value.minute(0).second(0).millisecond(0);
    }
    if (minutes < 45) {
        return value.minute(30).second(0).millisecond(0);
    }

    return value.add(1, "hour").minute(0).second(0).millisecond(0);
};

export const getDefaultInsuranceDateRange = () => {
    const pickup = roundToHalfHour(dayjs());
    const dropoff = roundToHalfHour(pickup.add(1, "day"));
    return [pickup, dropoff];
};
